import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useGetVendorMeQuery } from '../../store/vendorApi';

const BLUR_PATHS: string[] = [
  '/promoters',
  '/reviews',
  '/testimonials',
  '/use-cases',
  '/1to1-calls',
  '/vip-requests',
  '/add-promoter',
  '/crm-import',
  '/prospects',
  '/prospect',
  '/add-prospect',
  '/rewards',
  '/teams-sales',
  '/team-member',
  '/add-team-member',
  '/my-page',
  '/create-page',
];

function isPathBlurred(pathname: string): boolean {
  return BLUR_PATHS.some(
    (path) => pathname === path || pathname.startsWith(path + '/')
  );
}

const OnboardingBlurWrapper: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const { data: vendor, isLoading: isLoadingVendor } = useGetVendorMeQuery(
    undefined,
    { skip: user?.role !== 'vendor' }
  );

  const pathname = location.pathname;
  const isVendor = user?.role === 'vendor';
  const needsOnboarding = vendor && !vendor.onboardingCompleted;
  const pathShouldBlur = isPathBlurred(pathname);
  const shouldBlur = isVendor && needsOnboarding && pathShouldBlur;

  // While loading vendor, render outlet without blur to avoid flash
  if (isVendor && isLoadingVendor) {
    return <Outlet />;
  }

  if (!shouldBlur) {
    return <Outlet />;
  }

  return (
    <div className="relative min-h-[400px]">
      <div className="blur-sm pointer-events-none select-none">
        <Outlet />
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
        <div className="bg-white p-8 rounded-xl shadow-xl text-center max-w-md border border-gray-200">
          <p className="text-gray-700 text-lg mb-4">
            {t('shared.onboarding.completeAccess')}
          </p>
          <p className="text-gray-600 text-sm mb-6">
            {t('shared.onboarding.finishSetup')}
          </p>
          <Link
            to="/onboarding"
            className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            {t('shared.onboarding.completeOnboarding')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OnboardingBlurWrapper;
